import { EventLightVm, MatchPlayerVm } from '../../utils/api';
import { MatchResult, perGame } from './insightsClubSummary';
import {
    detectNewGkTrackingByTeam,
    resolveDefendingGkMatchPlayerId,
} from './insightsGoalkeeperZones';
import { playerDisplayName, sortEventsChronological } from './insightsMatchStats';
import {
    isPlaceholderPlayerName,
    PlayerSeasonRow,
    shootingPercentText,
} from './insightsPlayerSeason';
import { MatchBundle } from './insightsQueries';
import { classifyShot } from './insightsTeamSplits';

/*
 * TW-admin-like player boards from Arena light events.
 * Situation splits we can derive: Σ, 6v6 (regular), PP (extraPlayer), Pen, FB (counter).
 * GK saves/GA use the same attribution as insightsGoalkeeperZones (TW process-events).
 * Skip swim-off until light events expose them. Valorization is InsightsValorizationBoard.
 */

export type TwSitKey = 'total' | 'regular' | 'extraPlayer' | 'penalty' | 'counter';

export const TW_SIT_KEYS: TwSitKey[] = ['total', 'regular', 'extraPlayer', 'penalty', 'counter'];

export const TW_SIT_LABELS: Record<TwSitKey, string> = {
    total: 'Σ',
    regular: '6v6',
    extraPlayer: 'PP',
    penalty: 'Pen',
    counter: 'FB',
};

export interface TwShotBucket {
    goals: number;
    attempts: number;
    assists: number;
}

export interface TwSaveBucket {
    saves: number;
    /** attempts = saves + goals against attributed to this keeper (SOG faced). */
    attempts: number;
}

export interface TwPlayerBoardRow {
    playerId: number;
    name: string;
    capNumbers: number[];
    matches: number;
    shots: Record<TwSitKey, TwShotBucket>;
    saves: Record<TwSitKey, TwSaveBucket>;
    assists: number;
    blocks: number;
    steals: number;
    turnovers: number;
    exclusionsDrawn: number;
    exclusionsCommitted: number;
}

const emptyShot = (): TwShotBucket => ({ goals: 0, attempts: 0, assists: 0 });
const emptySave = (): TwSaveBucket => ({ saves: 0, attempts: 0 });

const emptyShots = (): Record<TwSitKey, TwShotBucket> => ({
    total: emptyShot(),
    regular: emptyShot(),
    extraPlayer: emptyShot(),
    penalty: emptyShot(),
    counter: emptyShot(),
});

const emptySaves = (): Record<TwSitKey, TwSaveBucket> => ({
    total: emptySave(),
    regular: emptySave(),
    extraPlayer: emptySave(),
    penalty: emptySave(),
    counter: emptySave(),
});

const emptyBoardRow = (playerId: number, name: string): TwPlayerBoardRow => ({
    playerId,
    name,
    capNumbers: [],
    matches: 0,
    shots: emptyShots(),
    saves: emptySaves(),
    assists: 0,
    blocks: 0,
    steals: 0,
    turnovers: 0,
    exclusionsDrawn: 0,
    exclusionsCommitted: 0,
});

const sitFromShot = (shot: { type?: string; isFastBreak?: boolean }): TwSitKey => {
    const t = classifyShot(shot as Parameters<typeof classifyShot>[0]);
    if (t === 'extraPlayer') return 'extraPlayer';
    if (t === 'penalty') return 'penalty';
    if (t === 'counter') return 'counter';
    return 'regular';
};

const bumpShot = (
    row: TwPlayerBoardRow,
    sit: TwSitKey,
    kind: 'attempt' | 'goal' | 'assist'
): void => {
    const apply = (bucket: TwShotBucket) => {
        if (kind === 'attempt') bucket.attempts += 1;
        if (kind === 'goal') {
            bucket.goals += 1;
            bucket.attempts += 1;
        }
        if (kind === 'assist') bucket.assists += 1;
    };
    apply(row.shots.total);
    apply(row.shots[sit]);
};

const bumpSave = (row: TwPlayerBoardRow, sit: TwSitKey, kind: 'save' | 'against'): void => {
    const apply = (bucket: TwSaveBucket) => {
        bucket.attempts += 1;
        if (kind === 'save') bucket.saves += 1;
    };
    apply(row.saves.total);
    apply(row.saves[sit]);
};

export const shotPctText = (goals: number, attempts: number): string =>
    shootingPercentText(goals, attempts);

export const savePctText = (saves: number, attempts: number): string =>
    attempts > 0 ? `${Math.round((saves / attempts) * 100)}%` : '-';

export const perGameText = (total: number, matches: number): string => perGame(total, matches);

export interface TwPlayerMatchLine {
    matchId: number;
    startDate?: Date;
    opponentName: string;
    isHome: boolean;
    board: TwPlayerBoardRow;
    /** Club result in that match when known. */
    result?: MatchResult;
    goalsFor?: number;
    goalsAgainst?: number;
}

/**
 * Per-match TW board rows for one player (newest first). Same attribution as buildTwPlayerBoards.
 */
export const buildTwPlayerMatchLines = (
    bundles: MatchBundle[],
    ourTeamIds: number[],
    playerId: number
): TwPlayerMatchLine[] => {
    const sorted = [...bundles].sort((a, b) => {
        const ta = a.detail.startDate ? new Date(a.detail.startDate).getTime() : 0;
        const tb = b.detail.startDate ? new Date(b.detail.startDate).getTime() : 0;
        return tb - ta;
    });

    const lines: TwPlayerMatchLine[] = [];
    sorted.forEach((bundle) => {
        const rows = buildTwPlayerBoards([bundle], ourTeamIds);
        const board = rows.find((r) => r.playerId === playerId);
        if (!board) return;
        const isHome = ourTeamIds.includes(bundle.detail.homeTeamId);
        const gf = isHome ? bundle.detail.homeTeamGoalsTotal : bundle.detail.awayTeamGoalsTotal;
        const ga = isHome ? bundle.detail.awayTeamGoalsTotal : bundle.detail.homeTeamGoalsTotal;
        lines.push({
            matchId: bundle.detail.id,
            startDate: bundle.detail.startDate,
            opponentName:
                (isHome ? bundle.detail.awayTeamDisplayName : bundle.detail.homeTeamDisplayName) ||
                'Unknown opponent',
            isHome,
            board,
            goalsFor: gf,
            goalsAgainst: ga,
            result: gf == null || ga == null ? undefined : gf > ga ? 'W' : gf < ga ? 'L' : 'D',
        });
    });
    return lines;
};

/**
 * Build TW-like boards from match bundles (our club only, placeholders skipped).
 * GK: saves always savedById; goals against via active defending GK when Starting
 * GK exists, else legacy last-saver + pending (same as collectGkZoneStats).
 */
export const buildTwPlayerBoards = (
    bundles: MatchBundle[],
    ourTeamIds: number[]
): TwPlayerBoardRow[] => {
    const byId = new Map<number, TwPlayerBoardRow>();

    const ensure = (mp: MatchPlayerVm): TwPlayerBoardRow | undefined => {
        if (mp.id == null || mp.playerId == null) return undefined;
        if (!ourTeamIds.includes(mp.teamId)) return undefined;
        const name = playerDisplayName(mp);
        if (isPlaceholderPlayerName(name)) return undefined;
        let row = byId.get(mp.playerId);
        if (!row) {
            row = emptyBoardRow(mp.playerId, name);
            byId.set(mp.playerId, row);
        }
        if (mp.capNumber != null && !row.capNumbers.includes(mp.capNumber)) {
            row.capNumbers.push(mp.capNumber);
        }
        return row;
    };

    bundles.forEach((bundle) => {
        const lineup = new Map<number, MatchPlayerVm>();
        (bundle.detail.players || []).forEach((mp) => {
            if (mp.id == null) return;
            lineup.set(mp.id, mp);
        });

        // Appearances from lineup.
        lineup.forEach((mp) => {
            const row = ensure(mp);
            if (row) row.matches += 1;
        });

        const newTracking = detectNewGkTrackingByTeam(bundle.events);
        const lastSaverByTeam = new Map<number, number>();
        const pendingAgainstByTeam = new Map<number, TwSitKey[]>();

        const creditGk = (mpId: number, sit: TwSitKey, kind: 'save' | 'against'): void => {
            const mp = lineup.get(mpId);
            const row = mp ? ensure(mp) : undefined;
            if (row) bumpSave(row, sit, kind);
        };

        const flushPending = (teamId: number, saverMpId: number): void => {
            const pending = pendingAgainstByTeam.get(teamId);
            if (!pending || pending.length === 0) return;
            if (!lineup.get(saverMpId)) {
                pendingAgainstByTeam.delete(teamId);
                return;
            }
            pending.forEach((sit) => creditGk(saverMpId, sit, 'against'));
            pendingAgainstByTeam.delete(teamId);
        };

        sortEventsChronological(bundle.events).forEach((event: EventLightVm) => {
            if (event.shot) {
                const sit = sitFromShot(event.shot);
                const shooterMp =
                    event.shot.takenById != null ? lineup.get(event.shot.takenById) : undefined;
                const shooter = shooterMp ? ensure(shooterMp) : undefined;

                if (shooter) {
                    if (event.shot.isGoal) bumpShot(shooter, sit, 'goal');
                    else bumpShot(shooter, sit, 'attempt');
                }

                if (event.shot.isGoal && event.shot.assistedById != null) {
                    const am = lineup.get(event.shot.assistedById);
                    const assist = am ? ensure(am) : undefined;
                    if (assist) {
                        assist.assists += 1;
                        bumpShot(assist, sit, 'assist');
                    }
                }

                if (event.shot.blockedById != null) {
                    const bm = lineup.get(event.shot.blockedById);
                    const blocker = bm ? ensure(bm) : undefined;
                    if (blocker) blocker.blocks += 1;
                }

                // --- GK: only shots against our club ---
                if (!ourTeamIds.includes(event.shot.teamId)) {
                    const isGoal = !!event.shot.isGoal;
                    const savedById = event.shot.savedById;

                    if (!isGoal && savedById != null) {
                        const saver = lineup.get(savedById);
                        if (saver && ourTeamIds.includes(saver.teamId)) {
                            flushPending(saver.teamId, savedById);
                            lastSaverByTeam.set(saver.teamId, savedById);
                            creditGk(savedById, sit, 'save');
                        }
                    } else if (isGoal) {
                        const defendingMpId = resolveDefendingGkMatchPlayerId(
                            event,
                            bundle.detail.homeTeamId,
                            bundle.detail.awayTeamId
                        );
                        const defendingRow =
                            defendingMpId != null ? lineup.get(defendingMpId) : undefined;
                        const defendingTeamId =
                            defendingRow?.teamId ??
                            (event.shot.teamId === bundle.detail.homeTeamId
                                ? bundle.detail.awayTeamId
                                : bundle.detail.homeTeamId);

                        const usesNew =
                            event.shootoutOrder != null || (defendingTeamId != null && newTracking.get(defendingTeamId) === true);

                        if (usesNew && defendingMpId != null && defendingRow) {
                            if (ourTeamIds.includes(defendingRow.teamId)) {
                                creditGk(defendingMpId, sit, 'against');
                            }
                        } else {
                            const lastSaverId = lastSaverByTeam.get(defendingTeamId);
                            if (lastSaverId != null) {
                                const lastSaver = lineup.get(lastSaverId);
                                if (lastSaver && ourTeamIds.includes(lastSaver.teamId)) {
                                    creditGk(lastSaverId, sit, 'against');
                                }
                            } else if (defendingTeamId != null) {
                                const pending = pendingAgainstByTeam.get(defendingTeamId) || [];
                                pending.push(sit);
                                pendingAgainstByTeam.set(defendingTeamId, pending);
                            }
                        }
                    }
                }
            }

            if (event.exclusion) {
                if (event.exclusion.excludedPlayerId != null) {
                    const em = lineup.get(event.exclusion.excludedPlayerId);
                    const excluded = em ? ensure(em) : undefined;
                    if (excluded) excluded.exclusionsCommitted += 1;
                }
                if (event.exclusion.fouledPlayerId != null) {
                    const fm = lineup.get(event.exclusion.fouledPlayerId);
                    const fouled = fm ? ensure(fm) : undefined;
                    if (fouled) fouled.exclusionsDrawn += 1;
                }
            }

            if (event.turnover) {
                if (event.turnover.lostPossesionPlayerId != null) {
                    const lm = lineup.get(event.turnover.lostPossesionPlayerId);
                    const lost = lm ? ensure(lm) : undefined;
                    if (lost) lost.turnovers += 1;
                }
                if (event.turnover.wonPossesionPlayerId != null) {
                    const wm = lineup.get(event.turnover.wonPossesionPlayerId);
                    const won = wm ? ensure(wm) : undefined;
                    if (won) won.steals += 1;
                }
            }
        });
    });

    return Array.from(byId.values()).sort(
        (a, b) =>
            b.shots.total.goals - a.shots.total.goals ||
            b.saves.total.saves - a.saves.total.saves ||
            a.name.localeCompare(b.name)
    );
};

/** Convert board row to season-shaped row for search / min-MP filters. */
export const boardToSeasonLike = (row: TwPlayerBoardRow): PlayerSeasonRow => ({
    playerId: row.playerId,
    name: row.name,
    capNumbers: row.capNumbers,
    matches: row.matches,
    goals: row.shots.total.goals,
    shots: row.shots.total.attempts,
    assists: row.assists,
    exclusionsDrawn: row.exclusionsDrawn,
    exclusionsCommitted: row.exclusionsCommitted,
    turnovers: row.turnovers,
    steals: row.steals,
    blocks: row.blocks,
    saves: row.saves.total.saves,
});
